import DOMPurify, { type DOMPurify as Purifier } from 'dompurify';

const REMOTE_URL = /^\s*(?:https?:)?\/\//i;
const CSS_REMOTE_URL = /url\(\s*(?:["']|&quot;)?\s*(?:https?:)?\/\//i;
/** Attributes that load an image: img/input src, table background, video poster, svg image href. */
const IMAGE_ATTRIBUTES = ['src', 'background', 'poster', 'xlink:href'] as const;
const LINK_TAGS = new Set(['A', 'AREA']);

export interface SanitizedEmail {
  html: string;
  /** The body references images on other servers; the frame never loads them. */
  hasRemoteImages: boolean;
}

let purifier: Purifier | null = null;
// Set by the hook during one synchronous sanitize call.
let remoteSeen = false;

function emailPurifier(): Purifier {
  if (purifier) return purifier;
  // A private instance: these hooks must not touch any other DOMPurify use.
  const instance = DOMPurify(window);
  instance.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element)) return;
    if (LINK_TAGS.has(node.tagName) && node.hasAttribute('href')) {
      // Never hand the opened page a window.opener back into the portal.
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    const isSvgImage = node.tagName.toLowerCase() === 'image';
    for (const name of [...IMAGE_ATTRIBUTES, ...(isSvgImage ? ['href'] : [])]) {
      if (REMOTE_URL.test(node.getAttribute(name) ?? '')) remoteSeen = true;
    }
    if (CSS_REMOTE_URL.test(node.getAttribute('style') ?? '')) remoteSeen = true;
  });
  purifier = instance;
  return instance;
}

/**
 * Sanitised body of a stored mailbox email (#3736): no scripts, forms, frames
 * or event handlers, and links that cannot reach back into the portal. Null
 * where DOMPurify cannot run; the caller then shows the text body instead of
 * unsanitised HTML.
 */
export function sanitizeEmailHtml(html: string): SanitizedEmail | null {
  if (!DOMPurify.isSupported) return null;
  const instance = emailPurifier();
  remoteSeen = false;
  // Emails keep their CSS in <head>; a body-only sanitize would drop it.
  const root = instance.sanitize(html, {
    WHOLE_DOCUMENT: true,
    RETURN_DOM: true,
    FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea', 'iframe', 'object', 'embed', 'base', 'meta', 'link'],
    FORBID_ATTR: ['srcset'],
  });
  if (!(root instanceof Element)) return null;
  const headStyles = [...root.querySelectorAll('head style')].map((style) => style.outerHTML).join('');
  const clean = headStyles + (root.querySelector('body')?.innerHTML ?? '');
  // <style> blocks are text, not attributes, so the hook does not see them.
  return { html: clean, hasRemoteImages: remoteSeen || CSS_REMOTE_URL.test(clean) };
}

/**
 * Remote images never load: the frame's CSP allows only `data:` images, so no
 * tracking pixel tells the sender the email was opened (the portal's own CSP
 * would block them anyway). Scripts never run: DOMPurify strips them,
 * `default-src 'none'` blocks them and the frame's sandbox has no
 * `allow-scripts`.
 */
export function emailFrameDocument(sanitizedBody: string): string {
  const csp = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:";
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><meta name="referrer" content="no-referrer"><base target="_blank"><style>body{margin:0;font-family:sans-serif;font-size:14px;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style></head><body>${sanitizedBody}</body></html>`;
}
